import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  body: [{
    h: '1. Información que Recopilamos',
    p: 'Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital "Legado de Sabores". Esto incluye tu nombre, correo electrónico y datos de pago, los cuales son procesados de forma segura por nuestro proveedor de pagos.'
  }, {
    h: '2. Uso de la Información',
    p: 'Tu información se utiliza exclusivamente para: procesar tu pedido, enviarte el acceso al producto digital, y enviarte actualizaciones relacionadas con tu compra. No compartimos ni vendemos tu información a terceros.'
  }, {
    h: '3. Seguridad de los Datos',
    p: 'Empleamos medidas de seguridad estándar de la industria para proteger tu información personal. Los pagos se procesan a través de plataformas certificadas con encriptación SSL.'
  }, {
    h: '4. Cookies',
    p: 'Este sitio puede usar cookies para mejorar tu experiencia de navegación. Puedes desactivarlas desde la configuración de tu navegador en cualquier momento.'
  }, {
    h: '5. Tus Derechos',
    p: 'Tienes derecho a solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento. Tu información se conserva únicamente mientras sea necesaria para los fines descritos.'
  }, {
    h: '6. Cambios a esta Política',
    p: 'Nos reservamos el derecho de actualizar esta política. Cualquier cambio será publicado en esta misma página.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  body: [{
    h: '1. Naturaleza del Producto',
    p: '"Legado de Sabores" es un producto digital descargable (eBook y plantillas). Al realizar la compra, recibirás acceso inmediato a los archivos digitales para descarga personal.'
  }, {
    h: '2. Licencia de Uso',
    p: 'Tu compra te otorga una licencia personal, no transferible y no exclusiva para usar el contenido. No está permitida la reventa, redistribución, copia o reproducción del material con fines comerciales.'
  }, {
    h: '3. Política de Reembolso',
    p: 'Debido a la naturaleza digital del producto y al acceso inmediato al contenido, las ventas son finales y no se ofrecen reembolsos una vez completada la descarga, salvo que la ley aplicable indique lo contrario.'
  }, {
    h: '4. Propiedad Intelectual',
    p: 'Todo el contenido —textos, diseños, plantillas, marcos metodológicos como el "Método de la Entrevista Gastronómica"— es propiedad intelectual de Legado de Sabores y está protegido por las leyes de derechos de autor.'
  }, {
    h: '5. Resultados',
    p: 'El éxito en la documentación y preservación de tus recetas depende de tu compromiso personal con el método. No garantizamos resultados específicos, ya que dependen de la dedicación de cada usuaria.'
  }, {
    h: '6. Limitación de Responsabilidad',
    p: 'No nos hacemos responsables por daños indirectos, incidentales o consecuentes derivados del uso del producto. Nuestra responsabilidad máxima se limita al precio pagado por el producto.'
  }, {
    h: '7. Aceptación',
    p: 'Al completar tu compra, aceptas estos términos en su totalidad.'
  }]
};
function Modal({
  type,
  onClose
}: {
  type: ModalType;
  onClose: () => void;
}) {
  useEffect(() => {
    if (type) {
      document.body.style.overflow = 'hidden';
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', handleEsc);
      return () => {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', handleEsc);
      };
    }
  }, [type, onClose]);
  const content = type === 'privacy' ? privacyContent : type === 'terms' ? termsContent : null;
  return <AnimatePresence data-id="element-14">
      {type && content && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={onClose} className="fixed inset-0 z-50 bg-heritage-coffee/70 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" role="dialog" aria-modal="true" aria-labelledby="modal-title" data-id="element-15">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} onClick={e => e.stopPropagation()} className="bg-heritage-cream w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" data-id="element-16">
            <div className="flex items-center justify-between px-6 md:px-8 py-5 border-b border-heritage-gold/20 bg-white/50" data-id="element-17">
              <h2 id="modal-title" className="font-serif text-2xl md:text-3xl font-bold text-heritage-coffee" data-id="element-18">
                {content.title}
              </h2>
              <button onClick={onClose} aria-label="Cerrar" className="w-10 h-10 rounded-full hover:bg-heritage-beige flex items-center justify-center transition-colors text-heritage-coffee" data-id="element-19">
                <X className="w-5 h-5" data-id="element-20" />
              </button>
            </div>

            <div className="overflow-y-auto px-6 md:px-8 py-6 space-y-6" data-id="element-21">
              {content.body.map((section, i) => <div key={i} data-id="element-22">
                  <h3 className="font-serif font-semibold text-lg text-heritage-terracotta mb-2" data-id="element-23">
                    {section.h}
                  </h3>
                  <p className="text-heritage-coffee/85 leading-relaxed" data-id="element-24">
                    {section.p}
                  </p>
                </div>)}
              <p className="text-sm text-heritage-coffee/60 italic pt-4 border-t border-heritage-gold/20" data-id="element-25">
                Última actualización:{' '}
                {new Date().toLocaleDateString('es-ES', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
              </p>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [modal, setModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-heritage-coffee text-heritage-cream/60 py-12 text-center" data-id="element-26">
        <div className="max-w-4xl mx-auto px-6" data-id="element-27">
          <p className="font-serif text-xl md:text-2xl text-heritage-gold mb-6 italic" data-id="element-28">
            "Tu familia te lo agradecerá por generaciones."
          </p>
          <div className="w-16 h-[1px] bg-heritage-cream/20 mx-auto mb-6" data-id="element-29"></div>

          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6 text-sm" data-id="element-30">
            <button onClick={() => setModal('privacy')} className="hover:text-heritage-gold transition-colors underline-offset-4 hover:underline" data-id="element-31">
              Política de Privacidad
            </button>
            <span className="text-heritage-cream/20" aria-hidden="true" data-id="element-32">
              •
            </span>
            <button onClick={() => setModal('terms')} className="hover:text-heritage-gold transition-colors underline-offset-4 hover:underline" data-id="element-33">
              Términos y Condiciones
            </button>
          </nav>

          <p className="text-sm" data-id="element-34">
            &copy; {new Date().getFullYear()} Legado de Sabores. Todos los
            derechos reservados.
          </p>
        </div>
      </footer>

      <Modal type={modal} onClose={() => setModal(null)} data-id="element-35" />
    </>;
}